"use client";

import type * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { BookingWizard } from "@/components/booking/BookingWizard";
import { useRouter, useSearchParams } from "next/navigation";
import { appendSearchToPath } from "@/lib/booking/search-params";
import {
  buildInclusionItems,
  parseBookingMiscellaneousParams,
} from "@/lib/booking/package-inclusions";
import { useAppDispatch } from "@/lib/redux/hooks";
import { setSelectedPackageDetails } from "@/lib/redux/features/user-selected-details/userSelectedDetailsSlice";
import { bookingRentalService } from "@/lib/services/booking-rental-service";
import { miscellaneousService } from "@/lib/services/miscellaneous-service";
import type { BookingRentalDataQueryParams } from "@/lib/types/booking-rental";
import type {
  MiscellaneousMasterDataItem,
  ProtectionPackage,
} from "@/lib/types/miscellaneous";
import { formatAud } from "@/lib/vehicles";
import { LucideCheck, LucideInfo, LucideLoader, LucideLoaderCircle, LucideX } from "lucide-react";
import { Button } from "../ui/button";

export const BookingStepTwo: React.FC = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const dispatch = useAppDispatch();
  const [packages, setPackages] = useState<ProtectionPackage[]>([]);
  const [miscellaneousMasterData, setMiscellaneousMasterData] = useState<
    MiscellaneousMasterDataItem[]
  >([]);
  const [selectedPackageKey, setSelectedPackageKey] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bookingTotal, setBookingTotal] = useState<number | null>(null);
  const [isRentalDataLoading, setIsRentalDataLoading] = useState(false);

  const requestParams = useMemo(
    () => parseBookingMiscellaneousParams(searchParams),
    [searchParams],
  );

  useEffect(() => {
    let isMounted = true;

    const loadPackages = async () => {
      if (!requestParams) {
        setError(
          "Missing required URL params for loading packages (pickup, return, dates, vehicle).",
        );
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const response =
          await miscellaneousService.fetchBookingMiscellaneous(requestParams);

        if (!response.success) {
          throw new Error(
            response.message || "Unable to load protection packages.",
          );
        }

        if (!isMounted) {
          return;
        }

        const fetchedPackages = response.data.packages ?? [];
        setPackages(fetchedPackages);
        setMiscellaneousMasterData(response.data.miscellaneous_master_data ?? []);
        setSelectedPackageKey((current) => current || fetchedPackages[0]?.key || "");
      } catch (loadError) {
        if (!isMounted) {
          return;
        }
        const message =
          loadError instanceof Error
            ? loadError.message
            : "Unable to load protection packages.";
        setError(message);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void loadPackages();

    return () => {
      isMounted = false;
    };
  }, [requestParams]);

  const rentalDataParams = useMemo<BookingRentalDataQueryParams | null>(() => {
    if (!requestParams || !selectedPackageKey) {
      return null;
    }

    return {
      pickup_location: requestParams.pickup_location,
      return_location: requestParams.return_location,
      from_date: requestParams.from_date,
      to_date: requestParams.to_date,
      from_time: requestParams.from_time,
      to_time: requestParams.to_time,
      selected_vehicle_type_id: requestParams.selected_vehicle_type_id,
      selected_package: selectedPackageKey,
    };
  }, [requestParams, selectedPackageKey]);

  useEffect(() => {
    let isMounted = true;

    const loadRentalData = async () => {
      if (!rentalDataParams || isLoading || error) {
        return;
      }

      try {
        setIsRentalDataLoading(true);
        const response =
          await bookingRentalService.fetchBookingRentalData(rentalDataParams);

        if (!response.success) {
          throw new Error(response.message || "Unable to load booking total.");
        }

        if (!isMounted) {
          return;
        }

        setBookingTotal(response.data.rental_charges_breakdown.total.amount);
      } catch {
        if (!isMounted) {
          return;
        }
        setBookingTotal(null);
      } finally {
        if (isMounted) {
          setIsRentalDataLoading(false);
        }
      }
    };

    void loadRentalData();

    return () => {
      isMounted = false;
    };
  }, [rentalDataParams, isLoading, error]);

  const selectedPackage = useMemo(
    () => packages.find((item) => item.key === selectedPackageKey) ?? null,
    [packages, selectedPackageKey],
  );

  const inclusionItems = useMemo(
    () => buildInclusionItems(miscellaneousMasterData, packages),
    [miscellaneousMasterData, packages],
  );

  const continueHref = useMemo(() => {
    const nextSearch = new URLSearchParams(searchParams.toString());
    if (selectedPackageKey) {
      nextSearch.set("selectedPackage", selectedPackageKey);
    }
    return appendSearchToPath("/booking/extras", nextSearch);
  }, [searchParams, selectedPackageKey]);

  const handleContinue = () => {
    if (!selectedPackage) {
      return;
    }

    dispatch(setSelectedPackageDetails(selectedPackage));
    router.push(continueHref);
  };

  const headerAction = error ? undefined : (
    <div className="flex items-center gap-6">
      {!isLoading && packages.length > 0 ? (
        <p className="text-sm text-gray-600">
          Total :{" "}
          {isRentalDataLoading ? (
            <span className="inline-block perspective-[400px]">
              <span
                className="animate-spin-horizontal text-2xl font-bold text-gray-900"
                aria-label="Loading total"
              >
                AU$
              </span>
            </span>
          ) : (
            <span className="text-2xl font-bold text-gray-900">
              AU${formatAud(bookingTotal ?? 0)}
            </span>
          )}
        </p>
      ) : null}
      <Button
        size="xl"
        onClick={handleContinue}
        disabled={isLoading || packages.length === 0}
      >
        Continue
      </Button>
    </div>
  );

  return (
    <BookingWizard step={2} headerAction={headerAction}>
      <div className="px-12 py-6">
        {isLoading ? (
          <div className="flex flex-col gap-4">
            <div className="flex justify-center">
              <LucideLoaderCircle className="animate-spin text-blue-300" size={48}/>
            </div>
            <p className="text-center text-lg font-medium text-gray-800">Loading protection packages...</p>
          </div>
        ) : error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 px-6 py-10 text-center text-sm text-red-700">
            {error}
          </div>
        ) : packages.length === 0 ? (
          <div className="rounded-xl border border-gray-200 bg-white px-6 py-10 text-center text-sm text-gray-500">
            No protection packages available right now.
          </div>
        ) : (
          <div className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
              {packages.map((pkg) => {
                const isSelected = pkg.key === selectedPackageKey;
                const isIncluded = pkg.price_per_day === null;

                return (
                  <article
                    key={pkg.key}
                    className={[
                      "rounded-xl border bg-white transition-all",
                      isSelected
                        ? "border-blue-500 ring-2 ring-blue-500"
                        : "border-slate-200 shadow-xs",
                    ].join(" ")}
                  >
                    <div className="">
                      <div className="flex flex-col justify-between gap-2 bg-slate-200 p-6 rounded-t-xl min-h-53">
                        <div className="flex items-start">
                          <h3 className="text-2xl font-bold text-gray-900">
                            {pkg.name}
                          </h3>
                        </div>
                        
                        <div className="flex flex-col gap-1">
                          <p className="text-sm font-semibold text-gray-700">
                            {pkg.deductible !== null
                              ? `Deductible : up to AU$ ${formatAud(pkg.deductible)}`
                              : "No deductible"}
                          </p>

                          {pkg.discount > 0 ? (
                            <span className="inline-flex w-fit rounded-full bg-green-100 px-3 py-0.5 text-xs font-semibold text-green-700 border-0.5 border-green-300">
                              -{pkg.discount}% online discount
                            </span>
                          ) : null}
                        </div>

                        <Button
                          type="button"
                          variant={isSelected ? "black" : "outline2"}
                          onClick={() => setSelectedPackageKey(pkg.key)}
                          className="mt-1 w-full"
                          size="lg"
                        >
                          {isSelected ? "Selected" : "Select package"}
                        </Button>
                      </div>
                      
                      <div className="px-6 py-4">
                        <div className="pb-2">
                          {isIncluded ? (
                            <p className="text-2xl font-bold text-gray-900">
                              Included
                            </p>
                          ) : (
                            <div className="flex items-end gap-1.5">
                              <span className="text-2xl font-bold text-gray-900">
                                AU$ {formatAud(pkg.price_per_day ?? 0)}
                              </span>
                              <span className="pb-1 text-sm text-gray-600">
                                / day
                              </span>
                              {pkg.original_price_per_day !== null ? (
                                <span className="pb-1 text-xs text-gray-600 font-medium line-through">
                                  ${formatAud(pkg.original_price_per_day)}
                                </span>
                              ) : null}
                            </div>
                          )}
                        </div>

                        <ul className="space-y-2.5 pt-1">
                          {inclusionItems.map(({ key, label }) => {
                            const enabled = pkg.miscellaneous_included[key] === true;
                            return (
                              <li
                                key={key}
                                className="flex items-start gap-1.5 text-sm text-gray-700"
                              >
                                {enabled ? (
                                  <LucideCheck
                                    size={18}
                                    className="mt-0.5 shrink-0 text-green-600"
                                  />
                                ) : (
                                  <LucideX
                                    size={18}
                                    className="mt-0.5 shrink-0 text-red-400"
                                  />
                                )}
                                <span className={enabled ? "" : "text-gray-400"}>
                                  {label}
                                </span>
                                <LucideInfo
                                  size={13}
                                  className="mt-0.5 shrink-0 text-gray-400"
                                />
                              </li>
                            );
                          })}
                        </ul>
                        {/* <button
                          type="button"
                          onClick={() => setSelectedPackageKey(pkg.key)}
                          aria-label={`Select ${pkg.name}`}
                          className={[
                            "mt-1 size-5 rounded-full border",
                            isSelected
                              ? "border-gray-900 bg-gray-900"
                              : "border-gray-300 bg-white",
                          ].join(" ")}
                        /> */}

                      </div>
                      
                    </div>
                  </article>
                );
              })}
            </div>

            <div className="rounded-xl  bg-gray-100 px-6 py-5">
              <h3 className="text-xl font-semibold text-gray-900">
                Your booking overview
              </h3>
              <ul className="mt-4 space-y-2">
                {selectedPackage ? (
                  inclusionItems
                    .filter(
                      ({ key }) => selectedPackage.miscellaneous_included[key] === true,
                    )
                    .map(({ key, label }) => (
                      <li
                        key={key}
                        className="flex items-start gap-2 text-md text-gray-700"
                      >
                        <LucideCheck size={18} className="mt-0.5 shrink-0 text-green-600" />
                        <span>{label}</span>
                      </li>
                    ))
                ) : (
                  <li className="text-sm text-gray-500">
                    Select a package to see included items.
                  </li>
                )}
              </ul>
            </div>
          </div>
        )}
      </div>
    </BookingWizard>
  );
};
